<template>
  <div
    id="rich-editor-contaniner"
    ref="editor"
    class="flex flex-col editor-container"
    :class="{ disabled, 'with-border': readOnly }"
    :style="controlPreview ? { pointerEvents: 'none' } : {}"
  >
    <div
      v-if="!disabled || readOnly"
      class="editor cursor-text"
      :style="{
        // height: height ? `${height}px` : undefined,
        minHeight: minHeight ? `${minHeight}px` : undefined,
        maxHeight: maxHeight ? `${maxHeight}px` : undefined,
      }"
      v-bind="$attrs"
      @click="handleEditorClick"
    >
      <FlotoScrollView
        :show-duration="0"
        class="flex flex-col"
        :style="{
          resize: 'vertical',
          // height: height ? `${height}px` : undefined,
          minHeight: minHeight ? `${minHeight}px` : undefined,
          maxHeight: maxHeight ? `${maxHeight}px` : undefined,
        }"
      >
        <div class="p-2 mt-1 flex flex-1">
          <EditorContent
            class="editor-content flex w-full flex-col"
            :editor="editor"
          />
        </div>
      </FlotoScrollView>
      <div v-show="hasResults" v-if="!disabled && enableMention" ref="mentions">
        <ul :class="mentionListClass" class="p-1 text-ellipsis">
          <li
            v-for="option in filteredMentions"
            :key="option.id"
            role="menu"
            class="ant-dropdown-menu-item rounded"
            style="margin-bottom: 2px"
            :class="
              currentActiveMentionId === option.id
                ? 'ant-dropdown-menu-item-selected'
                : ''
            "
          >
            <a @click="selectMention(option)">
              {{ option.name }}
            </a>
          </li>
        </ul>
      </div>
    </div>
    <!-- eslint-disable-next-line -->
    <div
      v-else
      class="editor cursor-text"
      :class="{
        'bg-white': !$attrs['no-bg-white'] && false,
      }"
      v-bind="$attrs"
    >
      <div class="px-2 mt-1 flex flex-1">
        <EditorContent
          class="editor-content flex w-full flex-col"
          :editor="editor"
        />
      </div>
    </div>
    <component
      :is="affixToolbar && !isFullscreen ? 'MAffix' : 'div'"
      @change="menuAffixed = $event"
    >
      <div v-if="$scopedSlots['attachments-list']">
        <slot name="attachments-list" />
      </div>
      <div
        class="flex flex-1 items-center justify-between editor-menu-bar-row"
        :class="{
          affixed: menuAffixed,
          'editor-menu-bar': !disabled || readOnly,
        }"
      >
        <div
          v-if="$scopedSlots['editor-bar-actions']"
          class="border flex-shrink-0"
        >
          <slot name="editor-bar-actions" />
        </div>
        <EditorMenuBar
          v-if="showToolbar"
          v-slot="{ commands, isActive, getMarkAttrs }"
          :editor="editor"
          :style="disabled && readOnly ? { pointerEvents: 'none' } : {}"
        >
          <div
            class="editor-toolbar-buttons ml-3 flex flex-wrap w-full"
            :style="{
              marginRight: $scopedSlots['editor-bar-actions'] ? '32px' : '0',
            }"
          >
            <EditorButton
              v-if="disallowedExtensions.indexOf('bold') === -1"
              class="mr-1"
              :title="$t('bold')"
              :class="{ 'is-active': isActive.bold() }"
              @click="commands.bold"
            >
              <MIcon name="bold" />
            </EditorButton>
            <EditorButton
              v-if="disallowedExtensions.indexOf('italic') === -1"
              class="mr-1"
              :title="$t('italic')"
              :class="{ 'is-active': isActive.italic() }"
              @click="commands.italic"
            >
              <MIcon name="italic" />
            </EditorButton>
            <EditorButton
              v-if="disallowedExtensions.indexOf('underline') === -1"
              class="mr-1"
              :title="$t('underline')"
              :class="{ 'is-active': isActive.underline() }"
              @click="commands.underline"
            >
              <MIcon name="underline" />
            </EditorButton>
            <EditorButton
              v-if="disallowedExtensions.indexOf('paragraph') === -1"
              class="mr-1"
              :title="$t('paragraph')"
              :class="{ 'is-active': isActive.paragraph() }"
              @click="commands.paragraph"
            >
              <MIcon name="paragraph" />
            </EditorButton>
            <FontSizeButton
              :value="activePageSize"
              @change="commands.font_size($event)"
            >
              <MIcon name="font-size" />
            </FontSizeButton>
            <FontFamilyButton
              v-if="disallowedExtensions.indexOf('font-family') === -1"
              class="mr-1"
              :value="activePageSize"
              @change="commands.font_family($event)"
            >
              <MIcon name="font-family" />
            </FontFamilyButton>
            <EditorButton
              v-for="level in [1, 2, 3]"
              :key="level"
              class="mr-1"
              :title="`${$t('heading')} ${level}`"
              :class="{ 'is-active': isActive.heading({ level }) }"
              @click="commands.heading({ level })"
            >
              <MIcon :name="`h${level}`" />
            </EditorButton>
            <EditorButton
              v-if="disallowedExtensions.indexOf('text-align') === -1"
              class="mr-1"
              :title="$t('align_left')"
              @click="commands.align_left"
            >
              <MIcon name="align-left" />
            </EditorButton>
            <EditorButton
              v-if="disallowedExtensions.indexOf('text-align') === -1"
              class="mr-1"
              :title="$t('align_center')"
              @click="commands.align_center"
            >
              <MIcon name="align-center" />
            </EditorButton>
            <EditorButton
              v-if="disallowedExtensions.indexOf('text-align') === -1"
              class="mr-1"
              :title="$t('align_right')"
              @click="commands.align_right"
            >
              <MIcon name="align-right" />
            </EditorButton>
            <EditorButton
              v-if="disallowedExtensions.indexOf('text-align') === -1"
              class="mr-1"
              :title="$t('align_justify')"
              @click="commands.align_justify"
            >
              <MIcon name="align-justify" />
            </EditorButton>
            <EditorButton
              v-if="disallowedExtensions.indexOf('bullet-list') === -1"
              class="mr-1"
              :title="$t('bullet_list')"
              :class="{ 'is-active': isActive.bullet_list() }"
              @click="commands.bullet_list"
            >
              <MIcon name="list" />
            </EditorButton>
            <EditorButton
              v-if="disallowedExtensions.indexOf('ordered-list') === -1"
              class="mr-1"
              :title="$t('ordered_list')"
              :class="{ 'is-active': isActive.ordered_list() }"
              @click="commands.ordered_list"
            >
              <MIcon name="list-ol" />
            </EditorButton>
            <EditorButton
              v-if="disallowedExtensions.indexOf('horizontal-rule') === -1"
              class="mr-1"
              :title="$t('hr')"
              @click="commands.horizontal_rule"
            >
              <MIcon name="horizontal-rule" />
            </EditorButton>
            <TableButton
              v-if="disallowedExtensions.indexOf('table') === -1"
              class="mr-1"
              :class="{ 'is-active': isActive.table() }"
              :commands="{
                createTable: commands.createTable,
                deleteTable: commands.deleteTable,
                addRowBefore: commands.addRowBefore,
                addRowAfter: commands.addRowAfter,
                deleteRow: commands.deleteRow,
                addColumnBefore: commands.addColumnBefore,
                addColumnAfter: commands.addColumnAfter,
                deleteColumn: commands.deleteColumn,
                toggleCellMerge: commands.toggleCellMerge,
                setCellAttr: commands.setCellAttr,
              }"
              :active="isActive.table()"
            >
              <MIcon name="table-grid" />
            </TableButton>
            <LinkButton
              v-if="disallowedExtensions.indexOf('link') === -1"
              :class="{ 'is-active': isActive.link() }"
              :link="getMarkAttrs('link').href"
              class="mr-1"
              :disabled="editor.state.selection.empty"
              @hide="() => editor.view.focus()"
              @add="commands.link({ href: $event })"
              @remove="commands.link({ href: null })"
            >
              <MIcon name="link" />
            </LinkButton>
            <EmbedButton
              v-if="disallowedExtensions.indexOf('video') === -1"
              class="mr-1"
              @hide="() => editor.view.focus()"
              @add="commands.iframe({ src: $event })"
            >
              <MIcon name="video" />
            </EmbedButton>
            <ImageButton
              v-if="disallowedExtensions.indexOf('image') === -1"
              class="mr-1"
              :use-public-api="usePublicApi"
              @hide="() => editor.view.focus()"
              @add="commands.image({ src: $event })"
            >
              <MIcon name="image" />
            </ImageButton>
            <TextColorButton
              class="mr-1"
              :tooltip="$t('text_color')"
              @hide="() => editor.view.focus()"
              @confirm="commands.text_color($event)"
            >
              <MIcon name="text-color" size="lg" />
            </TextColorButton>
            <TextColorButton
              class="mr-1"
              :tooltip="$t('text_highlight')"
              @hide="() => editor.view.focus()"
              @confirm="commands.text_highlight($event)"
            >
              <MIcon name="text-bg-color" size="lg" />
            </TextColorButton>
            <TextColorButton
              v-if="isActive.table()"
              class="mr-1"
              :tooltip="`${$t('table')} ${$t('background')}`"
              @hide="() => editor.view.focus()"
              @confirm="($event) => handleSetTableRowColor($event, commands)"
            >
              <MIcon name="background-color" size="lg" />
            </TextColorButton>
            <EditorButton
              v-if="disallowedExtensions.indexOf('full-screen') === -1"
              class="mr-1"
              :title="$t(fullScreenTooltip)"
              @click="handleFullScreen"
            >
              <MIcon :name="fullScreenIcon" />
            </EditorButton>
            <EditorButton
              v-if="disallowedExtensions.indexOf('undo') === -1"
              class="mr-1"
              :title="$t('undo')"
              @click="commands.undo"
            >
              <MIcon name="undo-alt" />
            </EditorButton>
            <EditorButton
              v-if="disallowedExtensions.indexOf('redo') === -1"
              class="mr-1"
              :title="$t('redo')"
              @click="commands.redo"
            >
              <MIcon name="redo" />
            </EditorButton>
          </div>
        </EditorMenuBar>
        <div
          v-if="$scopedSlots['submit-button']"
          class="submit-button-container flex-shrink-0"
        >
          <slot name="submit-button" />
        </div>
      </div>
    </component>
    <!-- <div v-else class="editor-content" v-bind="$attrs" v-html="value"></div> -->
  </div>
</template>
<script>
import api from '@api'
import { DOMParser } from 'prosemirror-model'
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import {
  Bold,
  Italic,
  HardBreak,
  Underline,
  OrderedList,
  BulletList,
  History,
  // ListItem,
  HorizontalRule,
  Placeholder,
  Mention,
  // Image,
  // Table,
  TableHeader,
  TableRow,
} from 'tiptap-extensions'
import UpdatedTableCell from './node/table-without-border'
import TextColor from './plugins/text-color'
import FontSize, { findActiveFontSize } from './plugins/font-size'
import FontFamily from './plugins/font-family'
import TextAlign from './plugins/text-align'
import TextHighlight from './plugins/text-highlight'
import { TechnicianComputed } from '@state/modules/technician'
import { authComputed } from '@state/modules/auth'
import { searchList } from '@utils/arr'
import './icons'
import Iframe from './node/iframe/iframe'
import Div from './node/div'
import Link from './marks/link'
import EditorButton from './editor-button'
import LinkButton from './link-button'
import EmbedButton from './embed-button'
import ImageButton from './image-button'
import TableButton from './table-button'
import FontSizeButton from './font-size-button'
import FontFamilyButton from './font-family-button'
import Paragraph from './node/paragraph'
import ListItem from './node/list-item'
import Heading from './node/heading'
import TextColorButton from './color-button'
import ImageUpload from './plugins/image-uploader'
import FormData from 'form-data'
import { makeFullScreen, exitFullScreen } from '@utils/fullscreen.js'
import ResizableTable from './extensions/ResizableTable'
import ResizableTableCell from './extensions/ResizableTableCell'

export default {
  name: 'FlotoRichEditor',
  components: {
    EditorContent,
    EditorMenuBar,
    EditorButton,
    LinkButton,
    ImageButton,
    EmbedButton,
    TableButton,
    FontSizeButton,
    TextColorButton,
    FontFamilyButton,
  },
  model: {
    event: 'update',
  },
  props: {
    controlPreview: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    readOnly: { type: Boolean, default: false },
    enableMention: { type: Boolean, default: false },
    rows: { type: [String, Number], default: undefined },
    minRows: { type: [String, Number], default: 8 },
    value: { type: String, default: undefined },
    placeholder: { type: String, default: '' },
    maxHeight: { type: Number, default: undefined },
    autoFocus: { type: Boolean, default: false },
    // eslint-disable-next-line
    affixToolbar: { type: Boolean, default: true },
    // eslint-disable-next-line
    toolbar: { type: Boolean, default: true },
    usePublicApi: { type: Boolean, default: false },
    disallowedExtensions: {
      type: Array,
      default() {
        return []
      },
    },
  },
  data() {
    return {
      isFullscreen: false,
      menuAffixed: false,
      editor: new Editor({
        parseOptions: {
          preserveWhitespace: true,
        },
        // onPaste: (view, event, slice) => {
        //   // @TODO remove this manual error throw when we find a way
        //   throw new Error('Just to prevent Scroll')
        //   // event.stopPropagation()
        //   // event.preventDefault()
        //   // return false
        // },
        disablePasteRules: true, // disable Markdown when pasting
        disableInputRules: true, // disable Markdown when typing
        onUpdate: ({ getHTML }) => {
          const newContent = getHTML()
          this.$emit('update', /^<p><\/p>$/.test(newContent) ? '' : newContent)
        },
        onBlur: (args) => {
          this.$emit('blur', this.value)
        },
        onFocus: () => {
          this.$emit('focus')
        },
        content: this.value || '',
        extensions: [
          new Div(),
          new Bold(),
          new Italic(),
          new Underline(),
          new Paragraph(),
          new Link(),
          new HardBreak(),
          new OrderedList(),
          new BulletList(),
          new ListItem(),
          new History(),
          new HorizontalRule(),
          new Iframe(),
          new TextColor(),
          new FontSize(),
          new TextAlign(),
          new TextHighlight(),
          new FontFamily(),
          // new Image(),
          new Heading({
            levels: [1, 2, 3],
          }),
          new Placeholder({
            emptyNodeClass: 'is-empty',
            emptyNodeText: this.placeholder,
            showOnlyWhenEditable: true,
          }),
          new ResizableTable(),
          new ResizableTableCell(),
          // new Table({
          //   resizable: true,
          // }),
          new TableHeader(),
          new UpdatedTableCell(),
          new TableRow(),
          ...this.getConditionalExtensions(),
          new ImageUpload({
            uploader: (image) => {
              // Method that handles the image upload and returns a url(string)
              return this.uploadImage(image)
            },
          }),
        ],
      }),
      query: null,
      suggestionRange: null,
      filteredMentions: [],
      currentActiveMentionItemIndex: 0,
      insertMention: () => {},
      mentionTriggerNode: null,
      tippyFn: null,
      fullScreenIcon: 'expand',
      fullScreenTooltip: 'full_screen',
      fontFamilyTooltip: 'font_family',
    }
  },
  computed: {
    ...TechnicianComputed,
    ...authComputed,
    showToolbar() {
      if (this.readOnly === true && this.disabled === true && this.toolbar) {
        return true
      }
      return this.disabled === false && this.toolbar
    },
    headers() {
      if (!this.loggedIn && this.publicAccessToken) {
        return { Authorization: 'Bearer ' + this.publicAccessToken }
      }
      if (this.accessToken) {
        return { Authorization: 'Bearer ' + this.accessToken }
      }
      return {}
    },
    mentionListClass() {
      // @TODO make this dynamic so we can have prefix dynamic
      return 'ant-dropdown-menu ant-dropdown-menu-vertical ant-dropdown-menu-root ant-dropdown-menu-light ant-dropdown-content'
    },
    height() {
      if (this.rows) {
        return parseInt(this.rows, 10) * 20
      }
      return undefined
    },
    minHeight() {
      if (!this.rows && !this.minRows) {
        return 5 * 20
      }
      if (this.minRows) {
        return parseInt(this.minRows, 10) * 20
      }
      return undefined
    },
    currentActiveMentionId() {
      return (this.filteredMentions[this.currentActiveMentionItemIndex] || {})
        .id
    },
    hasResults() {
      return this.filteredMentions.length
    },
    showSuggestions() {
      return this.query || this.hasResults
    },
    activePageSize() {
      return findActiveFontSize(this.editor.state)
    },
    activePageFontFamily() {
      return findActiveFontSize(this.editor.state)
    },
  },
  watch: {
    disabled: {
      immediate: true,
      handler(newValue) {
        this.editor.setOptions({
          editable: newValue === false,
        })
      },
    },
    value(newValue, oldValue) {
      if (this.disabled) {
        this.editor.setContent(newValue)
      }
    },
  },
  created() {
    import('tippy.js').then((d) => (this.tippyFn = d.default || d))
  },
  mounted() {
    if (this.autoFocus) {
      this.editor.focus('end')
    }
    this.editor.view.setProps({
      clipboardTextSerializer(slice) {
        return slice.content
          .textBetween(0, slice.content.size, '\n\n')
          .replace(/\n\n/g, '\n')
      },
    })
    document.addEventListener('fullscreenchange', this.checkFullscreen)
  },
  beforeDestroy() {
    this.tippyFn = null
    this.destroyMention()
    this.editor.destroy()
    // Clean up the event listener
    document.removeEventListener('fullscreenchange', this.checkFullscreen)
  },
  methods: {
    checkFullscreen() {
      this.isFullscreen = !!document.fullscreenElement
    },
    handleFullScreen() {
      if (this.isFullscreen) {
        exitFullScreen()
        this.fullScreenIcon = 'expand'
        this.fullScreenTooltip = 'full_screen'
        return
      }
      const element = this.$refs.editor
      if (element) {
        makeFullScreen(element)
        this.fullScreenIcon = 'collapse'
        this.fullScreenTooltip = 'exit_full_screen'
      }
    },
    uploadImage(img) {
      const form = new FormData()
      form.append('file', img)
      return api.post('/public/upload/file', form, {
        headers: { 'content-type': 'multipart/form-data', ...this.headers },
      })
    },
    focus(type = 'end') {
      this.editor.focus(type)
    },
    handleEditorClick() {
      if (!this.editor.view.focused) {
        this.editor.focus('end')
      }
    },
    addContentAtCurrentPosition(content) {
      const transaction = this.editor.state.tr.insertText(` ${content} `)
      this.editor.view.dispatch(transaction)
    },
    elementFromString(value) {
      const element = document.createElement('div')
      element.innerHTML = value.trim()

      return element
    },
    insertHTML(value) {
      const { state, view } = this.editor
      const { selection } = state
      const element = this.elementFromString(value)
      const slice = DOMParser.fromSchema(state.schema).parseSlice(element)
      const transaction = state.tr.insert(selection.anchor, slice.content)

      view.dispatch(transaction)
    },
    renderMentions(node) {
      if (this.popup) {
        this.popup.popperInstance.reference = {
          clientWidth: 0,
          clientHeight: 0,
          getBoundingClientRect() {
            return node.getBoundingClientRect()
          },
        }
        this.popup.popperInstance.scheduleUpdate()
        return
      }
      if (!this.tippyFn) {
        return
      }
      this.popup = this.tippyFn(node, {
        content: this.$refs.mentions,
        trigger: 'mouseenter',
        sticky: true,
        animateFill: false,
        interactive: true,
        theme: 'mentions',
        placement: 'top-start',
        inertia: true,
        duration: [400, 200],
        showOnInit: true,
      })
    },
    destroyMention() {
      if (this.popup) {
        this.popup.destroy()
        this.popup = null
      }
    },
    upHandler() {
      this.currentActiveMentionItemIndex =
        (this.currentActiveMentionItemIndex +
          this.filteredMentions.length -
          1) %
        this.filteredMentions.length
    },
    downHandler() {
      this.currentActiveMentionItemIndex =
        (this.currentActiveMentionItemIndex + 1) % this.filteredMentions.length
    },
    enterHandler() {
      const user = this.filteredMentions[this.currentActiveMentionItemIndex]
      if (user) {
        this.selectMention(user)
      }
    },
    selectMention(user) {
      this.insertMention({
        range: this.suggestionRange,
        attrs: {
          id: user.id,
          label: user.name,
        },
      })
      this.editor.focus()
    },
    getConditionalExtensions() {
      const extensions = []
      if (this.enableMention) {
        extensions.push(
          new Mention({
            items: () =>
              this.technicianOptions
                .filter((o) => o.id)
                .map((o) => ({
                  id: o.id,
                  name: `${o.name} `,
                })),
            onEnter: ({ items, query, range, command, virtualNode }) => {
              this.query = query
              this.filteredMentions = items
              this.suggestionRange = range
              this.renderMentions(virtualNode)
              this.insertMention = command
            },
            onChange: ({ items, query, range, virtualNode }) => {
              this.query = query
              this.filteredMentions = items
              this.suggestionRange = range
              this.currentActiveMentionItemIndex = 0
              this.renderMentions(virtualNode)
            },
            onExit: () => {
              this.query = null
              this.filteredMentions = []
              this.suggestionRange = null
              this.currentActiveMentionItemIndex = 0
              this.destroyMention()
            },
            onKeyDown: ({ event }) => {
              if (event.keyCode === 38) {
                event.stopPropagation()
                event.stopImmediatePropagation()
                this.upHandler()
                return true
              }
              if (event.keyCode === 40) {
                event.stopPropagation()
                event.stopImmediatePropagation()
                this.downHandler()
                return true
              }
              if (event.keyCode === 13) {
                event.stopPropagation()
                event.stopImmediatePropagation()
                this.enterHandler()
                return true
              }
              return false
            },
            onFilter: (items, query) => {
              if (!query) {
                return items
              }
              return searchList(items, query)
            },
          })
        )
      }
      return extensions
    },
    handleSetTableRowColor(event, commands) {
      commands.setCellAttr({
        name: 'background',
        value: event,
      })
    },
  },
}
</script>

<style lang="less" scoped>
.editor-container {
  &:not(.border-none) {
    @apply rounded;

    background-color: var(--page-background-color);
    border: 1px solid var(--border-color);
  }

  &.disabled {
    // stylelint-disable-next-line

    // color: var(--page-text-color);
    cursor: auto;
    background-color: var(--page-background-color);
  }

  &.disabled:not(.with-border) {
    @apply border-none;

    padding: 6px 6px 0;
  }

  .editor {
    // stylelint-disable-next-line
    .__panel {
      margin-right: -18px !important;
    }

    div:focus {
      outline: 0 !important;
    }
  }

  .editor-menu-bar {
    background-color: var(--page-background-color);
    border-top: 1px solid var(--border-color);
    border-bottom-right-radius: 8px;
    border-bottom-left-radius: 8px;
    //stylelint-disable-next-line
    padding: 10px;
    //stylelint-disable-next-line
    @apply shadow flex
      flex-wrap;

    &.affixed {
      // border: 1px solid var(--border-color);

      @apply shadow-lg;
    }
  }
  // .editor-menu-bar-bottom {
  //   border-bottom: 1px solid var(--border-color);

  //   @apply p-2 shadow flex flex-wrap;
  //   &.affixed {
  //     border: 1px solid var(--border-color);

  //     @apply shadow-lg;
  //   }
  // }
  .is-active,
  .@{ant-prefix}-btn:hover {
    // color: var(--rich-text-box-color) !important;
    // background-color: var(--rich-text-box-background-color) !important;
  }
}

.submit-button-container {
  display: flex;
  justify-content: flex-end;
  margin-right: 8px;
}
// stylelint-disable-next-line
.border-t {
  border-top: 1px solid var(--border-color);
}

.editor-menu-bar-row {
  // Prevent children from wrapping to new lines
  flex-wrap: nowrap;
  align-items: center;
  min-width: 0; // allow shrinking
}

.editor-toolbar-buttons {
  display: flex;
  flex: 1 1 0%;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  min-width: 0;
}
</style>
<style lang="less">
.editor-container {
  ul,
  ol {
    font-size: inherit; /* Ensure the font size of the list marker matches the list item */
  }

  li {
    font-size: var(
      --li-font-size,
      inherit
    ); /* Use a CSS variable to control font size */
  }
}
</style>
